import React, { useState, useRef, useCallback } from "react";
import bsaList from "../data/bsa.json";
import "../styles/Pages.css";
import "../styles/Vachanamrut.css";

//help : node scripts/build-bsa-index.js

const CHAPTERS = [...new Map(bsaList.map((b) => [b.chapter, b])).values()].map(
  (b) => ({ chapter: b.chapter, label: b.chaptername }),
);

function getSnippet(text, word) {
  const idx = text.toLowerCase().indexOf(word.toLowerCase());
  if (idx === -1)
    return text.slice(0, 160).trim() + (text.length > 160 ? "…" : "");
  const start = Math.max(0, idx - 80);
  const end = Math.min(text.length, idx + word.length + 80);
  let snippet = text.slice(start, end).trim();
  if (start > 0) snippet = "…" + snippet;
  if (end < text.length) snippet += "…";
  return snippet;
}

function Highlighted({ text, word }) {
  if (!word.trim()) return <>{text}</>;
  const regex = new RegExp(
    `(${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? <mark key={i}>{part}</mark> : part,
      )}
    </>
  );
}

export default function BhedeSakshiAnant() {
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [fontSize, setFontSize] = useState(1);

  const [wordQuery, setWordQuery] = useState("");
  const [wordResults, setWordResults] = useState(null);
  const [wordSearching, setWordSearching] = useState(false);
  const indexRef = useRef(null);

  const loadAndSearch = useCallback(async (query) => {
    if (!query.trim()) return;
    setWordSearching(true);
    setWordResults(null);

    try {
      if (!indexRef.current) {
        const res = await fetch(
          `${process.env.PUBLIC_URL}/data/bsa-index.json`,
        );
        if (!res.ok) throw new Error("Could not load search index");
        indexRef.current = await res.json();
      }

      const ql = query.trim().toLowerCase();
      const results = [];

      for (const entry of indexRef.current) {
        if (!entry.content.toLowerCase().includes(ql)) continue;
        const item = bsaList.find((b) => b.id === entry.id);
        if (!item) continue;
        results.push({
          item,
          snippet: getSnippet(entry.content, query.trim()),
        });
      }

      setWordResults(results);
    } catch (err) {
      setWordResults([]);
      console.error(err);
    } finally {
      setWordSearching(false);
    }
  }, []);

  const handleWordSearch = (e) => {
    e.preventDefault();
    loadAndSearch(wordQuery);
  };

  const clearWordSearch = () => {
    setWordQuery("");
    setWordResults(null);
  };

  const wordResultIds = wordResults
    ? new Set(wordResults.map((r) => r.item.id))
    : null;

  const filtered = bsaList.filter((b) => {
    if (selectedChapter && b.chapter !== selectedChapter) return false;
    if (wordResultIds !== null && !wordResultIds.has(b.id)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      b.prasangtitle?.toLowerCase().includes(q) ||
      b.personname?.toLowerCase().includes(q) ||
      b.chaptername?.toLowerCase().includes(q)
    );
  });

  const loadItem = useCallback((item) => {
    setSelected(item);
    setContent(null);
    setError(null);
    setLoading(true);
    setFontSize(1);
    const path = `${process.env.PUBLIC_URL}/data/bsa/${item.prasangfile}`;
    fetch(path)
      .then((res) => {
        if (!res.ok) throw new Error("Could not load file");
        return res.text();
      })
      .then((raw) =>
        setContent(
          raw
            .split(/\n\s*\n/)
            .map((p) => p.trim())
            .filter(Boolean),
        ),
      )
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleListSelect = (item) => {
    clearWordSearch();
    loadItem(item);
  };

  const handleResultClick = (item) => {
    clearWordSearch();
    loadItem(item);
  };

  return (
    <main className="page-content vachanamrut-page scripture-reader">
      <section className="page-header">
        <h1 className="section-title">Bhede Sakshi Anantna</h1>
        <p className="header-subtitle">ભેદે સાક્ષી અંતનતા</p>
      </section>

      {/* ── Full-text word search ── */}
      <form className="word-search-bar" onSubmit={handleWordSearch}>
        <input
          className="word-search-input"
          type="text"
          placeholder="Search word in text… (Gujarati or English)"
          value={wordQuery}
          onChange={(e) => setWordQuery(e.target.value)}
        />
        <button
          type="submit"
          className="word-search-btn"
          disabled={wordSearching}
        >
          {wordSearching ? "Searching…" : "Search"}
        </button>
        {wordResults !== null && (
          <button
            type="button"
            className="word-search-clear"
            onClick={clearWordSearch}
          >
            ✕ Clear
          </button>
        )}
      </form>

      {/* ── Word search results ── */}
      {wordResults !== null && (
        <div className="word-results-panel">
          {wordResults.length === 0 ? (
            <p className="word-results-empty">
              No results found for "{wordQuery}".
            </p>
          ) : (
            <>
              <p className="word-results-count">
                {wordResults.length} result{wordResults.length !== 1 ? "s" : ""}{" "}
                for "{wordQuery}"
              </p>
              <ul className="word-results-list">
                {wordResults.map(({ item, snippet }) => (
                  <li
                    key={item.id}
                    className="word-result-item"
                    onClick={() => handleResultClick(item)}
                  >
                    <div className="word-result-header">
                      <span className="vachan-name">
                        Pr.{item.chapter} · {item.prasangno}.{" "}
                        {item.prasangtitle}
                      </span>
                      <span className="vachan-name-guj">{item.personname}</span>
                    </div>
                    <p className="word-result-snippet">
                      <Highlighted text={snippet} word={wordQuery} />
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="vachanamrut-layout">
        {/* Chapter filter */}
        <div className="vachanamrut-chapters" role="group" aria-label="Chapters">
          <button
            className={`chapter-btn${!selectedChapter ? " active" : ""}`}
            aria-pressed={!selectedChapter}
            onClick={() => {
              setSelectedChapter(null);
              setSelected(null);
            }}
          >
            All
          </button>
          {CHAPTERS.map((ch) => (
            <button
              key={ch.chapter}
              className={`chapter-btn${selectedChapter === ch.chapter ? " active" : ""}`}
              aria-pressed={selectedChapter === ch.chapter}
              onClick={() => {
                setSelectedChapter(ch.chapter);
                setSelected(null);
              }}
            >
              {ch.label}
            </button>
          ))}
        </div>

        {/* List + Content */}
        <div className="vachanamrut-body">
          <aside className="vachanamrut-list-panel">
            <input
              className="vachanamrut-search"
              type="text"
              placeholder="Filter list…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <ul className="vachanamrut-list">
              {filtered.map((b) => (
                <li
                  key={b.id}
                  className={`vachanamrut-item${selected?.id === b.id ? " active" : ""}`}
                  aria-current={selected?.id === b.id ? "true" : undefined}
                  onClick={() => handleListSelect(b)}
                >
                  <span className="vachan-name">
                    {b.prasangno}. {b.prasangtitle}
                  </span>
                  <span className="vachan-name-guj">{b.personname}</span>
                </li>
              ))}
            </ul>
            {filtered.length === 0 && (
              <p className="reader-muted" style={{ textAlign: "center", color: "#888", padding: 24 }}>
                No entries found.
              </p>
            )}
          </aside>

          <section className="vachanamrut-content-panel">
            {!selected && (
              <div className="vachanamrut-placeholder">
                <p>વાંચવા માટે ડાબી બાજુથી પ્રસંગ પસંદ કરો.</p>
                <p>Select an entry from the list to read.</p>
              </div>
            )}

            {loading && <div className="vachanamrut-loading">Loading…</div>}
            {error && <div className="vachanamrut-error">Error: {error}</div>}

            {content && (
              <article className="vachanamrut-article">
                <div className="font-resize-bar">
                  <button
                    onClick={() => setFontSize((s) => Math.max(s - 0.15, 0.7))}
                  >
                    A-
                  </button>
                  <button
                    onClick={() => setFontSize((s) => Math.min(s + 0.15, 2.5))}
                  >
                    A+
                  </button>
                  <span>{Math.round(fontSize * 100)}%</span>
                </div>
                {selected && (
                  <div className="vachan-meta">
                    <span>{selected.chaptername}</span>
                    <span>{selected.personname}</span>
                  </div>
                )}
                {selected && (
                  <h2 className="vachan-title">{selected.prasangtitle}</h2>
                )}
                <div style={{ fontSize: `${fontSize}em` }}>
                  {content.map((para, i) => (
                    <p key={i} className="vachan-para">
                      <Highlighted text={para} word={wordQuery} />
                    </p>
                  ))}
                </div>
              </article>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
